import { configureStore, type Middleware, type Reducer } from '@reduxjs/toolkit';
import { type AuthState, authSlice } from './slices/auth';

/**
 * Options for creating the app store
 */
export interface CreateStoreOptions {
  /** Additional reducers to include in the store */
  additionalReducers?: Record<string, Reducer>;
  /** Additional middleware to include in the store */
  additionalMiddleware?: Middleware[];
  /** Preloaded state — used by E2E tests to inject auth without Auth0 */
  preloadedState?: { auth?: AuthState };
}

/**
 * Create a Redux store with the given reducers and middleware
 * Apps can extend this with their own slices and middleware
 */
export function createAppStore(options?: CreateStoreOptions) {
  const { additionalReducers, additionalMiddleware = [], preloadedState } = options || {};

  return configureStore({
    reducer: {
      auth: authSlice.reducer,
      ...additionalReducers,
    },
    preloadedState: preloadedState as { auth: AuthState } | undefined,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: ['persist/PERSIST', 'auth0/sync'],
          ignoredActionPaths: ['payload.userMetadata'],
          ignoredPaths: ['auth.user.userMetadata'],
        },
      }).concat(additionalMiddleware),
    devTools: import.meta.env?.MODE !== 'production',
  });
}

export type AppStore = ReturnType<typeof createAppStore>;
export type RootState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];
